import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, TableHead, TableBody, TableRow, TableCell, Paper, TableContainer,
  Button, TextField, Box, CircularProgress, IconButton, Dialog,
  DialogActions, DialogContent, DialogTitle, Typography, Fade, Chip,
  Avatar, Tooltip, Alert, Snackbar, Collapse, Card, Grid, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, ToggleButtonGroup, ToggleButton,
  TableSortLabel, Divider
} from '@mui/material';
import { useTheme, alpha, styled } from '@mui/material/styles';
import {
  Check as CheckIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Room as RoomIcon,
  CalendarMonth as CalendarIcon,
  SupervisorAccount as SupervisorIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import client from '../../../../../API';

// Styled Components
const GlassFilterBar = styled(Paper)(({ theme }) => ({
  backdropFilter: "blur(20px)",
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: 16,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
}));

const ModernTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 16,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
  '& .MuiTable-root': {
    minWidth: 650,
  },
  '& .MuiTableHead-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    '& .MuiTableCell-head': {
      fontWeight: 700,
      color: theme.palette.primary.main,
      borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      padding: theme.spacing(2),
    }
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      transform: 'scale(1.001)',
    }
  },
  '& .MuiTableCell-body': {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  }
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderColor: alpha(theme.palette.primary.main, 0.3),
    transform: 'scale(1.1)',
  }
}));

const ModernDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.9)})`,
    backdropFilter: "blur(20px)",
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  }
}));

const ClientDetailsView = () => {
  const theme = useTheme();
  const [clientData, setClientData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [supervisorFilter, setSupervisorFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  // Sort states
  const [sortBy, setSortBy] = useState('date_created'); // 'date_created', 'full_name', 'company', 'email'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  const [newClient, setNewClient] = useState({
    full_name: '',
    company: '',
    email: '',
    location: '',
    phone_number: '',
    bio: '',
    profilephoto: '',
    supervisor: ''
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Fetch clients from API
  const fetchClients = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await client.users.fetchClient();
      setClientData(res);
    } catch (err) {
      console.error("Error fetching clients:", err);
      setError(err);
      setSnackbar({ open: true, message: 'Failed to load clients', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Get unique supervisors for filter dropdown
  const uniqueSupervisors = useMemo(() => {
    const supervisors = new Set();
    clientData.forEach(client => {
      if (client.supervisor != null) {
        supervisors.add(client.supervisor);
      }
    });
    return Array.from(supervisors).sort((a, b) => a - b);
  }, [clientData]);

  // Get unique companies for suggestions
  const uniqueCompanies = useMemo(() => {
    const companies = new Set();
    clientData.forEach(client => {
      if (client.company) {
        companies.add(client.company);
      }
    });
    return Array.from(companies).sort();
  }, [clientData]);

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    let filtered = [...clientData];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(client => 
        client.full_name?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.company?.toLowerCase().includes(query) ||
        client.location?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(client => 
        statusFilter === 'active' ? client.status : !client.status
      );
    }

    // Apply supervisor filter
    if (supervisorFilter !== 'all') {
      filtered = filtered.filter(client => 
        String(client.supervisor) === String(supervisorFilter)
      );
    }

    // Apply company filter
    if (companyFilter) {
      filtered = filtered.filter(client => 
        client.company?.toLowerCase().includes(companyFilter.toLowerCase())
      );
    }

    // Apply location filter
    if (locationFilter) {
      filtered = filtered.filter(client => 
        client.location?.toLowerCase().includes(locationFilter.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'full_name':
          aValue = a.full_name?.toLowerCase() || '';
          bValue = b.full_name?.toLowerCase() || '';
          break;
        case 'company':
          aValue = a.company?.toLowerCase() || '';
          bValue = b.company?.toLowerCase() || '';
          break;
        case 'email':
          aValue = a.email?.toLowerCase() || '';
          bValue = b.email?.toLowerCase() || '';
          break;
        case 'date_created':
        default:
          aValue = new Date(a.date_created || 0).getTime();
          bValue = new Date(b.date_created || 0).getTime();
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [clientData, searchQuery, statusFilter, supervisorFilter, companyFilter, locationFilter, sortBy, sortOrder]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setSupervisorFilter('all');
    setCompanyFilter('');
    setLocationFilter('');
    setSortBy('date_created');
    setSortOrder('desc');
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || statusFilter !== 'all' || 
    supervisorFilter !== 'all' || companyFilter || locationFilter ||
    sortBy !== 'date_created' || sortOrder !== 'desc';

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Add a new client with optimistic update
  const addClient = async () => {
    if (!newClient.full_name || !newClient.email) {
      setSnackbar({ open: true, message: 'Name and email are required', severity: 'warning' });
      return;
    }

    const optimisticClient = {
      ...newClient,
      id: `temp-${Date.now()}`,
      date_created: new Date().toISOString(),
      status: false,
      last_project: null,
    };

    setClientData(prev => [...prev, optimisticClient]);
    setShowAddForm(false);
    setSnackbar({ open: true, message: 'Adding client...', severity: 'info' });

    try {
      const response = await client.users.postClient(newClient);
      
      setClientData(prev => prev.map(c => 
        c.id === optimisticClient.id ? { ...response, id: response.id || response.client_id } : c
      ));
      
      setNewClient({
        full_name: '',
        company: '',
        email: '',
        location: '',
        phone_number: '',
        bio: '',
        profilephoto: '',
        supervisor: ''
      });
      
      setSnackbar({ open: true, message: 'Client added successfully!', severity: 'success' });
    } catch (err) {
      console.error("Error adding client:", err);
      setClientData(prev => prev.filter(c => c.id !== optimisticClient.id));
      setSnackbar({ open: true, message: 'Failed to add client', severity: 'error' });
    }
  };

  const handleEditClick = (clientItem) => {
    setSelectedClient({
      ...clientItem,
      supervisor: clientItem.supervisor ?? ''
    });
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedClient(null);
  };

  const handleEditSubmit = async () => {
    if (!selectedClient) return;

    const originalClient = clientData.find(c => c.id === selectedClient.id);
    
    setClientData(prev => prev.map(c => 
      c.id === selectedClient.id ? selectedClient : c
    ));
    setEditDialogOpen(false);
    setSnackbar({ open: true, message: 'Updating client...', severity: 'info' });

    try {
      await client.users.updateClient(selectedClient.id, selectedClient);
      setSnackbar({ open: true, message: 'Client updated successfully!', severity: 'success' });
    } catch (err) {
      console.error("Error updating client:", err);
      setClientData(prev => prev.map(c => 
        c.id === selectedClient.id ? originalClient : c
      ));
      setSnackbar({ open: true, message: 'Failed to update client', severity: 'error' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewClient(prev => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setSelectedClient(prev => ({ ...prev, [name]: value }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading && clientData.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ 
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5
            }}>
              Client Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredAndSortedClients.length} of {clientData.length} clients
              {hasActiveFilters && ' (filtered)'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ borderRadius: 2 }}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddForm(!showAddForm)}
              sx={{ 
                borderRadius: 2,
                px: 3,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              {showAddForm ? 'Cancel' : 'Add Client'}
            </Button>
          </Box>
        </Box>

        {/* Filter Bar */}
        <Collapse in={showFilters}>
          <GlassFilterBar>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Filters & Sorting
              </Typography>
              {hasActiveFilters && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  color="error"
                >
                  Clear All
                </Button>
              )}
            </Box>
            <Grid container spacing={2}>
              {/* Search */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name, email, company, location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              {/* Status Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Supervisor Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Supervisor</InputLabel>
                  <Select
                    value={supervisorFilter}
                    onChange={(e) => setSupervisorFilter(e.target.value)}
                    label="Supervisor"
                  >
                    <MenuItem value="all">All Supervisors</MenuItem>
                    {uniqueSupervisors.map(sup => (
                      <MenuItem key={sup} value={sup}>
                        Supervisor #{sup}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Company Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Company"
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  InputProps={{
                    endAdornment: companyFilter && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setCompanyFilter('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              {/* Location Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Location"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  InputProps={{
                    endAdornment: locationFilter && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setLocationFilter('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              {/* Sort By */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Sort By"
                  >
                    <MenuItem value="date_created">Date Created</MenuItem>
                    <MenuItem value="full_name">Name</MenuItem>
                    <MenuItem value="company">Company</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Sort Order */}
              <Grid item xs={12} sm={6} md={3}>
                <ToggleButtonGroup
                  value={sortOrder}
                  exclusive
                  onChange={(e, val) => val && setSortOrder(val)}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="asc">
                    <ArrowUpwardIcon fontSize="small" sx={{ mr: 1 }} />
                    Ascending
                  </ToggleButton>
                  <ToggleButton value="desc">
                    <ArrowDownwardIcon fontSize="small" sx={{ mr: 1 }} />
                    Descending
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </GlassFilterBar>
        </Collapse>

        {/* Add Client Form */}
        <Collapse in={showAddForm}>
          <GlassFilterBar sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
              Add New Client
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="full_name"
                  label="Full Name *"
                  value={newClient.full_name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="email"
                  label="Email *"
                  type="email"
                  value={newClient.email}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="company"
                  label="Company"
                  value={newClient.company}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="location"
                  label="Location"
                  value={newClient.location}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="phone_number"
                  label="Phone Number"
                  value={newClient.phone_number}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="supervisor"
                  label="Supervisor ID"
                  type="number"
                  value={newClient.supervisor}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="profilephoto"
                  label="Profile Photo URL"
                  value={newClient.profilephoto}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="bio"
                  label="Bio"
                  value={newClient.bio}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  multiline
                  rows={1}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => setShowAddForm(false)}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={addClient}
                    startIcon={<SaveIcon />}
                  >
                    Add Client
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </GlassFilterBar>
        </Collapse>

        {/* Clients Table */}
        {filteredAndSortedClients.length > 0 ? (
          <ModernTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'full_name'}
                      direction={sortBy === 'full_name' ? sortOrder : 'asc'}
                      onClick={() => handleSortChange('full_name')}
                    >
                      Client
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'company'}
                      direction={sortBy === 'company' ? sortOrder : 'asc'}
                      onClick={() => handleSortChange('company')}
                    >
                      Company
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'email'}
                      direction={sortBy === 'email' ? sortOrder : 'asc'}
                      onClick={() => handleSortChange('email')}
                    >
                      Contact
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Supervisor</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'date_created'}
                      direction={sortBy === 'date_created' ? sortOrder : 'asc'}
                      onClick={() => handleSortChange('date_created')}
                    >
                      Date Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Last Project</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedClients.map(item => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <StyledAvatar src={item.profilephoto}>
                          {item.full_name?.[0]}
                        </StyledAvatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {item.full_name || '—'}
                          </Typography>
                          {item.bio && (
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}>
                              {item.bio}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.company ? (
                        <Chip 
                          icon={<BusinessIcon fontSize="small" />}
                          label={item.company} 
                          size="small" 
                          variant="outlined"
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {item.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="caption">{item.email}</Typography>
                          </Box>
                        )}
                        {item.phone_number && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="caption">{item.phone_number}</Typography>
                          </Box>
                        )}
                        {!item.email && !item.phone_number && '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.location ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <RoomIcon fontSize="small" color="action" />
                          <Typography variant="body2">{item.location}</Typography>
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {item.supervisor != null ? (
                        <Chip 
                          icon={<SupervisorIcon fontSize="small" />}
                          label={`#${item.supervisor}`} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {new Date(item.date_created).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {item.status ? (
                        <Tooltip title="Connected">
                          <CheckIcon sx={{ color: 'success.main' }} />
                        </Tooltip>
                      ) : (
                        <Chip label="Inactive" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.last_project || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit Client">
                        <ActionButton size="small" onClick={() => handleEditClick(item)}>
                          <EditIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ModernTableContainer>
        ) : clientData.length > 0 ? (
          <Card sx={{ 
            p: 6, 
            textAlign: 'center', 
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)',
          }}>
            <FilterListIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No clients match your filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Try adjusting your search criteria or clearing filters
            </Typography>
            <Button 
              variant="outlined" 
              startIcon={<ClearIcon />}
              onClick={clearFilters}
            >
              Clear All Filters
            </Button>
          </Card>
        ) : (
          <Card sx={{ 
            p: 6, 
            textAlign: 'center', 
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)',
          }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No clients yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Get started by adding your first client
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setShowAddForm(true)}
            >
              Add First Client
            </Button>
          </Card>
        )}

        {/* Edit Client Dialog */}
        <ModernDialog open={editDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle sx={{ 
            fontWeight: 700, 
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pb: 2 
          }}>
            Edit Client
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="full_name"
                  label="Full Name"
                  value={selectedClient?.full_name || ''}
                  onChange={handleEditChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={selectedClient?.email || ''}
                  onChange={handleEditChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="company"
                  label="Company"
                  value={selectedClient?.company || ''}
                  onChange={handleEditChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="location"
                  label="Location"
                  value={selectedClient?.location || ''}
                  onChange={handleEditChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="phone_number"
                  label="Phone Number"
                  value={selectedClient?.phone_number || ''}
                  onChange={handleEditChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="supervisor"
                  label="Supervisor ID"
                  type="number"
                  value={selectedClient?.supervisor ?? ''}
                  onChange={handleEditChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="profilephoto"
                  label="Profile Photo URL"
                  value={selectedClient?.profilephoto || ''}
                  onChange={handleEditChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="bio"
                  label="Bio"
                  value={selectedClient?.bio || ''}
                  onChange={handleEditChange}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ pr: 3, pb: 3, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button onClick={handleDialogClose} startIcon={<CloseIcon />}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleEditSubmit}
              startIcon={<SaveIcon />}
            >
              Save Changes
            </Button>
          </DialogActions>
        </ModernDialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ 
              borderRadius: 2,
              boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default ClientDetailsView;